import React, { useCallback, useEffect, useReducer, useRef } from 'react'
import { useLDClient } from 'launchdarkly-react-client-sdk'
import { useHistory } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import { AuthClient } from './auth-client'
import AuthContext from './auth-context'
import { hasAuthParams, AUTH_PARAMS } from './auth-utils'
import { reducer } from './auth-reducer'
import { initialAuthState } from './auth-state'
import { isAuthError, AuthErrorCodes } from './auth-errors'
import { tokenRefreshService } from './token-refresh-service'
import { userActivity } from './user-activity'
import authLogger from './auth-logger'
import { getCurrentUser, unsetCurrentUser } from '../actions/currentUser'
import { selectCurrentUser } from '../reducers/currentUser/currentUserSelectors'
import { getLDContext, defaultAnonymousContext } from '../helpers/IdHelpers'
import analytics from '../helpers/segmentAnalytics'
import LoadingSpinner from '../components/Shared/Loading/LoadingSpinner'

const AuthProvider = ({ children }) => {
  const storeDispatch = useDispatch()
  const history = useHistory()
  const ldClient = useLDClient()
  const didInitialise = useRef(false)
  const clientRef = useRef(null)
  const [state, dispatch] = useReducer(reducer, initialAuthState)

  // User data lives in Redux — single source of truth
  const currentUser = useSelector(selectCurrentUser)

  const sessionStartHandler = useCallback(
    async (expiresIn) => {
      await storeDispatch(getCurrentUser(true))
      dispatch({ type: 'AUTHENTICATED' })
      tokenRefreshService.scheduleRefresh(expiresIn)
    },
    [storeDispatch],
  )

  const sessionEndHandler = useCallback(async () => {
    tokenRefreshService.teardown()
    storeDispatch(unsetCurrentUser())
    dispatch({ type: 'UNAUTHENTICATED' })
  }, [storeDispatch])

  const onAuthenticationError = useCallback((error, notify = true) => {
    if (notify) {
      if (isAuthError(error)) {
        dispatch({ type: 'SET_ERROR', error: error.message })
      } else {
        dispatch({ type: 'SET_ERROR', error: 'Login failed. Please try logging in again.' })
      }
    }
  }, [])

  const clearError = useCallback(() => {
    dispatch({ type: 'CLEAR_ERROR' })
  }, [])

  const login = useCallback(
    async ({ username, password }) => {
      try {
        const result = await clientRef.current.login({ username, password })
        dispatch({ type: 'CLEAR_ERROR' })
        await sessionStartHandler(result?.expiresIn)
      } catch (error) {
        onAuthenticationError(error)
        throw error
      }
    },
    [sessionStartHandler, onAuthenticationError],
  )

  const impersonationLogin = useCallback(
    async (token) => {
      try {
        const result = await clientRef.current.impersonationLogin(token)
        dispatch({ type: 'CLEAR_ERROR' })
        await sessionStartHandler(result?.expiresIn)
      } catch (error) {
        onAuthenticationError(error)
        await sessionEndHandler()
      }
    },
    [sessionStartHandler, sessionEndHandler, onAuthenticationError],
  )

  const endSession = useCallback(async () => {
    try {
      await clientRef.current.logout()
    } catch (error) {
      onAuthenticationError(error, false)
    } finally {
      await sessionEndHandler()
    }
  }, [onAuthenticationError, sessionEndHandler])

  const logout = useCallback(async () => {
    history.replace('/logout')
  }, [history])

  const handleExistingSession = useCallback(async () => {
    try {
      const result = await clientRef.current.checkSession()
      dispatch({ type: 'CLEAR_ERROR' })
      await sessionStartHandler(result?.expiresIn)
    } catch (error) {
      onAuthenticationError(error, false)
      await sessionEndHandler()
    }
  }, [sessionStartHandler, sessionEndHandler, onAuthenticationError])

  const handleRedirectCallback = useCallback(async () => {
    const urlParams = new URLSearchParams(window.location.search)
    const code = urlParams.get(AUTH_PARAMS.LOGIN_CODE)
    const token = urlParams.get(AUTH_PARAMS.AUTH_TOKEN)

    try {
      if (code) {
        authLogger.debug('login with code from redirect')
        const result = await clientRef.current.loginWithCode(code)
        dispatch({ type: 'CLEAR_ERROR' })
        await sessionStartHandler(result?.expiresIn)
      } else if (token) {
        await impersonationLogin(token)
      } else {
        return
      }
      window.history.replaceState({}, document.title, window.location.pathname)
    } catch (error) {
      onAuthenticationError(error)
      await sessionEndHandler()
    }
  }, [impersonationLogin, sessionStartHandler, sessionEndHandler, onAuthenticationError])

  const initializeAuth = useCallback(async () => {
    if (didInitialise.current) {
      return
    }
    didInitialise.current = true

    authLogger.debug('Initialising auth provider')

    clientRef.current = new AuthClient()

    try {
      if (hasAuthParams()) {
        authLogger.debug('login from redirect params')
        await handleRedirectCallback()
      } else {
        authLogger.debug('checking existing session')
        await handleExistingSession()
      }
    } catch (error) {
      dispatch({ type: 'UNAUTHENTICATED' })
    }
  }, [handleExistingSession, handleRedirectCallback])

  // Initialize auth immediately on mount
  useEffect(() => {
    initializeAuth()
    return () => {
      if (clientRef.current) {
        clientRef.current.destroy()
      }
    }
  }, [initializeAuth])

  // Configure token refresh service. Refresh errors with SESSION_EXPIRED end the session.
  useEffect(() => {
    tokenRefreshService.configure({
      refreshFn: () => clientRef.current.refreshToken(),
      onRefreshError: (error) => {
        if (isAuthError(error) && error.code === AuthErrorCodes.SESSION_EXPIRED) {
          logout()
        }
      },
    })
  }, [logout])

  // Track user activity so the refresh timer can skip refreshes for inactive users.
  useEffect(() => {
    userActivity.start()
    return () => {
      userActivity.stop()
    }
  }, [])

  // Sync external services (LD + Segment) reactively when user state changes
  useEffect(() => {
    if (!state.isInitialized) return

    if (currentUser?.id) {
      authLogger.info('identifying user for external services', { userId: currentUser.id })
      analytics.userLogIn(currentUser)
      if (ldClient) {
        ldClient.identify(getLDContext(currentUser))
      }
    } else {
      authLogger.info('resetting external service identity')
      analytics.userLogOut()
      if (ldClient) {
        ldClient.identify(defaultAnonymousContext)
      }
    }
  }, [ldClient, currentUser, state.isInitialized])

  const contextValue = {
    ...state,
    user: currentUser,
    login,
    logout,
    endSession,
    impersonationLogin,
    clearError,
  }

  if (!state.isInitialized) {
    return <LoadingSpinner />
  }

  return <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>
}

export default AuthProvider
